import api from "./axiosClient";
import type { Tenant, TodoCcRule, TodoEmailRoutingConfig } from "../types/Tenant";
import type { Branch } from "../types/Branch";
import type {
    MonitoringPostCallIncident,
    MonitoringPostCallIncidentSummary,
} from "../types/Monitoring";

const base = '/tenants';

export type CallRetentionConfig = {
    callRetentionEnabled: boolean;
    callRetentionDays: number;
    callRetentionFields: string[];
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
    value && typeof value === "object" && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;

const asString = (value: unknown): string | null => {
    if (typeof value !== "string") return null;
    const normalized = value.trim();
    return normalized || null;
};

const asNumber = (value: unknown): number | null =>
    typeof value === "number" && Number.isFinite(value) ? value : null;

const asBoolean = (value: unknown): boolean | null =>
    typeof value === "boolean" ? value : null;

const extractPayload = (value: unknown): unknown => {
    const record = asRecord(value);
    if (!record) return value;
    if (Array.isArray(record.data)) return record.data;
    if (Array.isArray(record.result)) return record.result;
    if (Array.isArray(record.items)) return record.items;
    if (asRecord(record.data)) return record.data;
    if (asRecord(record.result)) return record.result;
    if (asRecord(record.summary)) return record.summary;
    return value;
};

const normalizePostCallIncident = (value: unknown): MonitoringPostCallIncident | null => {
    const record = asRecord(value);
    if (!record) return null;

    return {
        id: asString(record.id) || asString(record._id) || asString(record.key) || "unknown",
        message: asString(record.message) || asString(record.reason) || "Unknown incident",
        level: asString(record.level),
        category: asString(record.category) || asString(record.type),
        firstSeenAt: asString(record.firstSeenAt) || asString(record.createdAt),
        lastSeenAt: asString(record.lastSeenAt) || asString(record.updatedAt) || asString(record.createdAt),
        count: asNumber(record.count) || asNumber(record.occurrences) || 1,
        tenantId: asString(record.tenantId),
        tenantName: asString(record.tenantName),
        tenantDomain: asString(record.tenantDomain),
        agentId: asString(record.agentId),
        webhookId: asString(record.webhookId) || asString(record.tenantWebhookId),
        webhookSecretFingerprint: asString(record.webhookSecretFingerprint),
        resolutionSource: asString(record.resolutionSource),
        conversationId: asString(record.conversationId),
        signatureTimestamp: asString(record.signatureTimestamp),
        signatureAgeSeconds: asNumber(record.signatureAgeSeconds),
        sharedAgentConflictSuspected: asBoolean(record.sharedAgentConflictSuspected) === true,
        hookAccepted: asBoolean(record.hookAccepted),
        hookProcessingContinues: asBoolean(record.hookProcessingContinues),
        environment: asString(record.environment),
        publicBaseUrl: asString(record.publicBaseUrl),
        requestHost: asString(record.requestHost),
        requestOrigin: asString(record.requestOrigin),
    };
};

const normalizePostCallIncidentSummary = (value: unknown): MonitoringPostCallIncidentSummary => {
    const record = asRecord(value);
    if (!record) {
        return {
            activeCount: 0,
            totalCount: 0,
            lastSeenAt: null,
            affectedTenants: 0,
            affectedAgents: 0,
            sharedAgentConflictCount: 0,
        };
    }

    return {
        activeCount: asNumber(record.activeCount) || asNumber(record.openCount) || asNumber(record.count) || 0,
        totalCount: asNumber(record.totalCount) || asNumber(record.activeCount) || asNumber(record.openCount) || 0,
        lastSeenAt: asString(record.lastSeenAt),
        affectedTenants: asNumber(record.affectedTenants) || 0,
        affectedAgents: asNumber(record.affectedAgents) || 0,
        sharedAgentConflictCount: asNumber(record.sharedAgentConflictCount) || 0,
    };
};

const tenantService = {
    async getTenants(options: { view?: "list" } = {}): Promise<Tenant[]> {
        return (await api.get(`${base}`, {
            params: options.view ? { view: options.view } : undefined,
        })).data;
    },
    async getTenant(tenantId: string, options: { view?: "dashboard" | "todos" | "editor" | "agent-config" } = {}): Promise<Tenant> {
        return (await api.get(`${base}/${tenantId}`, {
            params: options.view ? { view: options.view } : undefined,
        })).data;
    },
    async getTenantBranches(tenantId: string): Promise<Branch[]> {
        return (await api.get(`${base}/${tenantId}/branches`)).data;
    },
    async createTenant(tenant: Partial<Tenant>): Promise<Tenant> {
        return (await api.post(`${base}`, tenant)).data;
    },
    async updateTenant(tenantId: string, tenant: Partial<Tenant>): Promise<Tenant> {
        return (await api.put(`${base}/${tenantId}`, tenant)).data;
    },
    async createTenantBranch(tenantId: string, branch: Branch): Promise<Branch> {
        return (await api.post(`${base}/${tenantId}/branches`, branch)).data;
    },
    async updateTenantBranch(tenantId: string, branchId: string, branch: Branch): Promise<Branch> {
        return (await api.put(`${base}/${tenantId}/branches/${branchId}`, branch)).data;
    },
    async deleteTenantBranch(tenantId: string, branchId: string): Promise<void> {
        await api.delete(`${base}/${tenantId}/branches/${branchId}`);
    },
    async updateActiveStatusTenant(tenantId: string, active: boolean): Promise<void> {
        await api.put(`${base}/${tenantId}/active`, { active });
    },
    async getCallRetention(tenantId: string): Promise<CallRetentionConfig> {
        return (await api.get(`${base}/${tenantId}/call-retention`)).data;
    },
    async getTodoEmailRouting(tenantId: string): Promise<TodoEmailRoutingConfig> {
        return (await api.get(`${base}/${tenantId}/todo-email-routing`)).data;
    },
    async updateTodoEmailRouting(
        tenantId: string,
        payload: { redirections?: TodoEmailRoutingConfig["redirections"]; ccRules?: TodoCcRule[] }
    ): Promise<TodoEmailRoutingConfig> {
        return (await api.put(`${base}/${tenantId}/todo-email-routing`, payload)).data;
    },
    async runCallRetention(tenantId: string): Promise<void> {
        await api.post(`${base}/${tenantId}/call-retention/run`);
    },
    async resetCallSyncState(tenantId: string): Promise<{ success: boolean; tenantId: string; tenantName: string; deletedCount: number }> {
        return (await api.post(`${base}/${tenantId}/call-sync-state/reset`)).data;
    },
    async getTenantLogoByDomain(domain: string): Promise<{
        logoUrl_normal: string;
    }> {
        return (await api.get(`${base}/logo?domain=${domain}`)).data;
    },
    async monitorCallQuote(): Promise<{
        tenantId: string,
        tenantName: string,
        interval: string,
        callQuote: number,
        callCount: number,
        from_unix: number,
        to_unix: number
    }[]> {
        return (await api.get(`${base}/monitoring/monitor-call-quote`)).data;
    },
    async getCallCount(): Promise<(Tenant & { call_count: number })[]> {
        return (await api.get(`${base}/monitoring/get-call-count`)).data.result;
    },
    async getPostCallMonitoringIncidents(): Promise<MonitoringPostCallIncident[]> {
        const response = await api.get(`${base}/monitoring/postcall-hook-incidents`);
        const payload = extractPayload(response.data);
        if (!Array.isArray(payload)) return [];
        return payload
            .map((item) => normalizePostCallIncident(item))
            .filter((item): item is MonitoringPostCallIncident => Boolean(item));
    },
    async getPostCallMonitoringSummary(): Promise<MonitoringPostCallIncidentSummary> {
        const response = await api.get(`${base}/monitoring/postcall-hook-incidents/summary`);
        return normalizePostCallIncidentSummary(extractPayload(response.data));
    },
    async syncReferencedAgents(tenantId?: string): Promise<{
        scope: "single-tenant" | "all-tenants";
        requestedTenantId: string | null;
        tenants: number;
        referencedAgents: number;
        missingAgents: number;
        syncedAgents: number;
        updatedAgentSettings: number;
        filledPhoneNumbers: number;
        filledNames: number;
        failures: number;
        tenantSummaries: Array<Record<string, unknown>>;
    }> {
        return (await api.post(`${base}/sync-referenced-agents`, tenantId ? { tenantId } : {})).data;
    }
}

export default tenantService;
